import React, {useEffect, useState} from "react";
import {TabView, TabPanel} from "primereact/tabview";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";
import {confirmDialog, ConfirmDialog} from "primereact/confirmdialog";
import {Dropdown} from "primereact/dropdown";

import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {PricingPlanService} from "../../service/PricingPlanService";
import {UserService} from "../../service/UserService";
import AdminPageHeader from "./AdminPageHeader";
import PricingPlanForm from "./PricingPlanForm";

const PricingPlans = () => {

    const {t, toast, setError} = React.useContext(AppContext);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [addCategory, setAddCategory] = useState('standard');
    const [users, setUsers] = useState([]);
    const [, setTick] = useState(0);
    const rerender = () => setTick(t => t + 1);
    const [convertDialogVisible, setConvertDialogVisible] = useState(false);
    const [convertPlan, setConvertPlan] = useState(null);
    const [convertTargetId, setConvertTargetId] = useState(null);

    const service = new PricingPlanService();
    const userService = new UserService();

    useEffect(() => {
        search();
    }, []);

    const search = () => {
        setLoading(true);
        service.getAllPlans().then(data => {
            setPlans(data);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoading(false);
        });
    };

    const standardPlans = plans.filter(p => !p.user_id && p.platform !== 'shopify');
    const shopifyPlans = plans.filter(p => p.platform === 'shopify');
    const specialPlans = plans.filter(p => !!p.user_id);

    const specialPlanUserIds = new Set(specialPlans.map(p => p.user_id));
    const userOptions = users
        .filter(u => !specialPlanUserIds.has(u.id))
        .map(u => ({value: u.id, label: u.email || u.id}));
    const CATEGORY_OPTIONS = [
        {value: 'standard', label: t('pricing_plan.tab.standard')},
        {value: 'shopify', label: t('pricing_plan.tab.shopify')},
        {value: 'special', label: t('pricing_plan.tab.special')},
    ];
    const DEFAULT_TYPE_BY_CATEGORY = {standard: 'basic', shopify: 'paid', special: 'special'};

    const ensureUsers = () => {
        if (users.length === 0) {
            userService.getUsers(false, false, true)
                .then(data => setUsers(data || []))
                .catch(error => { setError(error); setError(null); });
        }
    };

    const openAdd = () => {
        const category = activeIndex === 1 ? 'shopify' : activeIndex === 2 ? 'special' : 'standard';
        const base = {additional_features: [], is_active: true, type: DEFAULT_TYPE_BY_CATEGORY[category]};
        if (category === 'shopify') base.platform = 'shopify';
        setAddCategory(category);
        setIsAdding(true);
        setEditingPlan(base);
        setDialogVisible(true);
        if (category === 'special') ensureUsers();
    };

    const openEdit = (rowData) => {
        setIsAdding(false);
        // clone so cancel discards in-place edits made by the form
        setEditingPlan(JSON.parse(JSON.stringify(rowData)));
        setDialogVisible(true);
    };

    const onCategoryChange = (category) => {
        setAddCategory(category);
        editingPlan.platform = category === 'shopify' ? 'shopify' : null;
        if (category !== 'special') editingPlan.user_id = null;
        if (category === 'special') ensureUsers();
        editingPlan.type = DEFAULT_TYPE_BY_CATEGORY[category];
        rerender();
    };

    const onUserChange = (userId) => {
        editingPlan.user_id = userId;
        rerender();
    };

    const savePlan = () => {
        if (isAdding && addCategory === 'special' && !editingPlan.user_id) {
            toast.current.show({severity: 'warn', summary: t('pricing_plan.add'), detail: t('pricing_plan.select_user')});
            return;
        }
        service.addPricingPlan(editingPlan).then(() => {
            setDialogVisible(false);
            setEditingPlan(null);
            toast.current.show({severity: 'success', summary: t('pricing_plan.title'), detail: t('message_detail.successful')});
            search();
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const deletePlan = (rowData) => {
        confirmDialog({
            message: t('pricing_plan.confirm_delete'),
            header: t('actions.delete'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                service.deletePricingPlan(rowData.id).then(() => {
                    toast.current.show({severity: 'success', summary: t('actions.delete'), detail: t('message_detail.successful')});
                    search();
                }).catch(error => {
                    setError(error);
                    setError(null);
                });
            }
        });
    };

    const toggleActive = (rowData) => {
        const call = rowData.is_active ? service.deactivatePlan(rowData.id) : service.activatePlan(rowData.id);
        call.then(() => {
            search();
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const convertTargetOptions = standardPlans
        .filter(p => p.type !== 'trial')
        .map(p => ({value: p.id, label: p.name}));

    const openConvert = (rowData) => {
        setConvertPlan(rowData);
        setConvertTargetId(null);
        setConvertDialogVisible(true);
    };

    const doConvert = () => {
        if (!convertTargetId) {
            toast.current.show({severity: 'warn', summary: t('pricing_plan.convert'), detail: t('pricing_plan.select_plan')});
            return;
        }
        userService.convertSpecialSubscription({id: convertPlan.subscription_id}, convertTargetId).then(() => {
            setConvertDialogVisible(false);
            setConvertPlan(null);
            toast.current.show({severity: 'success', summary: t('pricing_plan.convert'), detail: t('message_detail.successful')});
            search();
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const nameTemplate = (rowData) => {
        return <span>
            {rowData.name}
            {rowData.type === 'trial' && <Tag value={t('pricing_plan.trial_badge')} severity="info" style={{marginLeft: '0.5em'}}/>}
        </span>;
    };

    const activeTemplate = (rowData) => {
        return <Tag value={rowData.is_active ? t('pricing_plan.active') : '-'}
                    severity={rowData.is_active ? 'success' : 'danger'}/>;
    };

    const subscribedTemplate = (rowData) => {
        return <Tag value={rowData.is_subscribed ? t('pricing_plan.subscribed') : t('pricing_plan.not_subscribed')}
                    severity={rowData.is_subscribed ? 'success' : 'warning'}/>;
    };

    const actionsTemplate = (rowData, isSpecial) => {
        return <>
            <Button icon="pi pi-pencil" className="p-button-text" onClick={() => openEdit(rowData)}/>
            <Button icon={rowData.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'} className="p-button-text"
                    onClick={() => toggleActive(rowData)}/>
            <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => deletePlan(rowData)}/>
            {isSpecial && <Button icon="pi pi-angle-double-right" className="p-button-text"
                    tooltip={t('pricing_plan.convert')}
                    disabled={!rowData.is_subscribed}
                    onClick={() => openConvert(rowData)}/>}
        </>;
    };

    const planTable = (value, isSpecial) => {
        return <DataTable value={value} emptyMessage={t('actions.empty_message')} sortField="monthly_price" sortOrder={1}>
            <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
            <Column field="name" header={t('table_headers.name')} body={nameTemplate} sortable/>
            {isSpecial && <Column field="user_email" header={t('pricing_plan.user')}/>}
            {isSpecial && <Column field="is_subscribed" header={t('pricing_plan.subscribed')} body={subscribedTemplate}/>}
            <Column field="monthly_price" header={t('subscription.monthly_price')} sortable/>
            <Column field="annual_price" header={t('subscription.annual_price')} sortable/>
            <Column field="total_product_count" header={t('subscription.total_product_count')}/>
            <Column field="total_competitor_count" header={t('subscription.total_competitor_count')}/>
            <Column field="is_active" header={t('pricing_plan.active')} body={activeTemplate}/>
            <Column body={(rowData) => actionsTemplate(rowData, isSpecial)} headerStyle={{width: '13em'}}/>
        </DataTable>;
    };

    return <React.Fragment>
        <ConfirmDialog/>
        <div className="card management-page">
            <AdminPageHeader
                title={t('pricing_plan.title')}
                subtitle={t('menu.management_hub.page.pricing_plans')}
                actions={<>
                    <Button label={t('actions.refresh')} icon="pi pi-refresh" className="p-button-outlined"
                            onClick={search} disabled={loading}/>
                    <Button label={t('pricing_plan.add')} icon="pi pi-plus" className="p-button-success"
                            onClick={openAdd} style={{marginLeft: '0.5em'}}/>
                </>}
            />

            {loading ?
                <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    <TabPanel header={t('pricing_plan.tab.standard')}>{planTable(standardPlans, false)}</TabPanel>
                    <TabPanel header={t('pricing_plan.tab.shopify')}>{planTable(shopifyPlans, false)}</TabPanel>
                    <TabPanel header={t('pricing_plan.tab.special')}>{planTable(specialPlans, true)}</TabPanel>
                </TabView>}

            <Dialog visible={dialogVisible} header={editingPlan && editingPlan.id ? t('pricing_plan.edit') : t('pricing_plan.add')}
                    modal style={{width: '60vw'}} onHide={() => setDialogVisible(false)}
                    footer={<>
                        <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-text"
                                onClick={() => setDialogVisible(false)}/>
                        <Button label={t('actions.save')} icon="pi pi-save" className="p-button-success"
                                onClick={savePlan}/>
                    </>}>
                {editingPlan && <>
                    {isAdding &&
                    <div className="p-grid p-fluid" style={{marginBottom: '0.5rem'}}>
                        <div className="p-col-6">
                            <label htmlFor="plan_category">{t('pricing_plan.category')}</label>
                            <Dropdown id="plan_category" value={addCategory} options={CATEGORY_OPTIONS}
                                      onChange={(e) => onCategoryChange(e.value)}/>
                        </div>
                        {addCategory === 'special' &&
                        <div className="p-col-6">
                            <label htmlFor="plan_user">{t('pricing_plan.user')}</label>
                            <Dropdown id="plan_user" value={editingPlan.user_id ?? null} options={userOptions} filter
                                      placeholder={t('pricing_plan.select_user')}
                                      onChange={(e) => onUserChange(e.value)}/>
                        </div>}
                    </div>}
                    <PricingPlanForm plan={editingPlan}
                        category={isAdding ? addCategory : (editingPlan.user_id ? 'special' : editingPlan.platform === 'shopify' ? 'shopify' : 'standard')}/>
                </>}
            </Dialog>
            <Dialog visible={convertDialogVisible} header={t('pricing_plan.convert')}
                    modal style={{width: '30vw'}} onHide={() => setConvertDialogVisible(false)}
                    footer={<>
                        <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-text"
                                onClick={() => setConvertDialogVisible(false)}/>
                        <Button label={t('pricing_plan.convert')} icon="pi pi-angle-double-right" className="p-button-success"
                                onClick={doConvert}/>
                    </>}>
                <div className="p-fluid">
                    <label htmlFor="convert_target">{t('pricing_plan.convert_target')}</label>
                    <Dropdown id="convert_target" value={convertTargetId} options={convertTargetOptions} filter
                              placeholder={t('pricing_plan.select_plan')}
                              onChange={(e) => setConvertTargetId(e.value)}/>
                </div>
            </Dialog>
        </div>
    </React.Fragment>;
};

export default PricingPlans;
